import { type ReactNode, useRef, useState, useEffect } from 'react';
import { Camera } from 'lucide-react';
import { SectionHeader } from '@/components/ui/section-header';

const FRAME = 'aspect-[9/16] w-32 rounded-lg transition-all';

function Placeholder(): ReactNode {
  return (
    <div
      className={`${FRAME} flex items-center justify-center bg-surface ring-1 ring-white/10`}
    >
      <Camera size={24} className="text-text-tertiary" />
    </div>
  );
}

function Preview({ src }: { src: string }): ReactNode {
  return (
    <img
      src={src}
      alt="Preview"
      className={`${FRAME} object-cover ring-1 ring-white/10`}
    />
  );
}

type Props = {
  photo: globalThis.File | null;
  onPick: (f: globalThis.File | null) => void;
};

function usePreviewUrl(file: globalThis.File | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setUrl(null);
      return;
    }
    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);
  return url;
}

function PhotoCaption({
  photo,
  onClear,
}: {
  photo: globalThis.File | null;
  onClear: () => void;
}): ReactNode {
  return (
    <div className="space-y-xs text-xs text-text-tertiary">
      <p>Foto + @ Instagram → la cuenta entra en test phase.</p>
      <p>
        Si dejas alguno vacío, el jugador completa lo que falte al loguearse.
      </p>
      {photo && (
        <button
          type="button"
          onClick={onClear}
          className="text-error hover:text-error/80"
        >
          Quitar foto
        </button>
      )}
    </div>
  );
}

export function PhotoSection({ photo, onPick }: Props): ReactNode {
  const ref = useRef<HTMLInputElement>(null);
  const preview = usePreviewUrl(photo);
  return (
    <div className="space-y-sm">
      <SectionHeader label="Foto de verificación" badge />
      <div className="flex items-center gap-md">
        <button type="button" onClick={() => ref.current?.click()}>
          {preview ? <Preview src={preview} /> : <Placeholder />}
        </button>
        <PhotoCaption photo={photo} onClear={() => onPick(null)} />
      </div>
      <input
        ref={ref}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => onPick(e.target.files?.[0] ?? null)}
      />
    </div>
  );
}
